import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPlayerAcceptanceBucket, getAcceptanceBucket, sortPlayers, sortTeams } from '../../../../utils/draws';
import { acceptanceBucketTypes, acceptanceBucketTypeSortKey, gameTypeValues, waitlistBucketTypeLabels } from '../../../../utils/eventConstants';
import { getPlayerPosition, sortType, getComparatorArguments } from '../../../../utils';
import { comparator } from '../../../../utils/comparator';

export const MAX_INITIAL_LOAD = 59;

const useSortFilterPlayersTeams = ({ filters, playerListFilter, divisions, players, teams, filteredDivisions, utrType }) => {
    const { acceptance, organizerRatings, playersDictByMemberId, drawsDict } = useSelector(({ drawEdit }) => {
        const { acceptance, organizerRatings, players, selected, selectedTeams, event, draws } = drawEdit;
        return {
            acceptance,
            organizerRatings,
            playersDict: players.playersDict,
            playersDictByMemberId: players.playersDictByMemberId,
            selected,
            selectedTeams,
            divisionsDict: event.divisionsDict,
            drawsDict: draws.drawsDict,
        };
    });
    const [filteredSortedTeams, setFilteredSortedTeams] = useState([]);
    const [maxSortedTeams, setMaxSortedTeams] = useState([]);
    const [filteredSortedPlayers, setFilteredSortedPlayers] = useState([]);
    const [maxSortedPlayers, setMaxSortedPlayers] = useState([]);
    const [sortState, setSortState] = useState({ sortParam: 'regStatus', sortType: 'asc' });
    const [hasLoadMore, setHasLoadMore] = useState(false);
    const [hasLoadMoreButton, setHasLoadMoreButton] = useState(false);

    const filter = playerListFilter.replace(/[-[\]{}()*+?.,\\/^$|#\s]/g, '\\$&');
    const filterRegex = new RegExp(filter, 'i');
    const isSingles = !filters.playerType || filters.playerType === gameTypeValues.SINGLES;

    // PLAYERS

    const selectedDiv = filters.selectedDivs.length === 1 && divisions.find(d => d.id === filters.selectedDivs[0]);

    const getPlayerType = (playerPosition) => {
        if (playerPosition) {
            const type = playerPosition.bucket === waitlistBucketTypeLabels.WAITLIST ? `#${playerPosition.position}` : '';
            return `${playerPosition.bucket} ${type}`;
        }

        return '';
    };

    useEffect(() => {
        const filteredPlayers = isSingles ? players
            // filter by filters
            .filter(player => {
                let showPlayer = true;
                // filter by name
                if (playerListFilter && !(`${player.firstName} ${player.lastName}`).match(filterRegex)) {
                    showPlayer = false;
                }
                // filter by gender
                if (filters.gender && player.gender !== filters.gender) {
                    showPlayer = false;
                }
                // filter by date
                if (filters.selectedDates.length) {
                    if (!player.divisions.some(div => filters.selectedDates.includes(div.specificDivisionDateUtc))) {
                        showPlayer = false;
                    }
                }
                return showPlayer;
            })
            // filter by draws
            .filter(player => {
                // not in draw
                if (filters.notInDraw) {
                    return !player.draws?.length;
                }
                // is in selected draws
                if (filters.selectedDraws.length) {
                    return player.draws.some(d => {
                        const drawsDictObj = drawsDict[d.id];
                        if (drawsDictObj.isChildDraw) {
                            return filters.selectedDraws.some(sd => sd === drawsDictObj.parentDrawId);
                        }
                        return filters.selectedDraws.some(sd => sd === d.id);
                    });
                }
                return true;
            })
            // filter by divisions
            .filter(player => {
                // if specific divisions are selected
                if (filters.selectedDivs.length) {
                    return player.divisions.some(d => filters.selectedDivs.some(sd => sd === d.id));
                }
                return true;
            }) : [];

        // special cases if 1 division is selected
        if (selectedDiv) {
            if (isSingles) {
                // if acceptance list, add acceptance info to player objects (bucket type and wildcard state id)
                if (acceptance?.[filters.selectedDivs[0]]?.loaded) {
                    const alternateBucket = getAcceptanceBucket(acceptance[filters.selectedDivs[0]], acceptanceBucketTypes.ALTERNATE);
                    const maxNumber = alternateBucket?.players ? [...alternateBucket.players.map(p => p.position), 0] : [0];
                    let alternateBucketMaxPosition = alternateBucket ? Math.max(maxNumber) : 0;
                    filteredPlayers.forEach(p => {
                        const bucket = getPlayerAcceptanceBucket(acceptance[filters.selectedDivs[0]], p.memberId);
                        if (bucket) {
                            const bucketPlayerObj = bucket.players.find(bp => bp.memberId === p.memberId);
                            p.regStatus = bucket?.type;
                            p.regStatusSortKey = acceptanceBucketTypeSortKey[bucket?.type];
                            p.acceptanceListWildcardStateId = bucketPlayerObj.acceptanceListWildcardStateId;
                            p.acceptanceBucketPosition = bucketPlayerObj?.position;
                        } else {
                            p.regStatus = acceptanceBucketTypes.ALTERNATE;
                            p.regStatusSortKey = acceptanceBucketTypeSortKey[acceptanceBucketTypes.ALTERNATE];
                            p.acceptanceListWildcardStateId = null;
                            alternateBucketMaxPosition += 1;
                            p.acceptanceBucketPosition = alternateBucketMaxPosition;
                            p.showPopOverOptions = false;
                        }
                    });
                }
            }
        }

        const filteredTeams = isSingles ? [] : teams
            // filter by filters
            .filter(team => {
                let showTeam = true;
                // filter by name
                if (playerListFilter && !team.searchableName?.match(filterRegex)) {
                    showTeam = false;
                }
                // filter by gender - is done by filtering the division list

                // filter by date
                if (filters.selectedDates.length) {
                    if (!team.divisions.some(div => filters.selectedDates.includes(div.specificDivisionDateUtc))) {
                        showTeam = false;
                    }
                }
                return showTeam;
            })
            // filter by draws
            .filter(team => {
                // not in draw
                if (filters.notInDraw) {
                    return !team.draws?.length;
                }
                // is in selected draws
                if (filters.selectedDraws.length) {
                    return team.draws.some(d => {
                        const drawsDictObj = drawsDict[d.id];
                        if (drawsDictObj.isChildDraw) {
                            return filters.selectedDraws.some(sd => sd === drawsDictObj.parentDrawId);
                        }
                        return filters.selectedDraws.some(sd => sd === d.id);
                    });
                }
                return true;
            })
            // filter by divisions
            .filter(team => {
                // if no specific divisions are selected, filter player list by the filteredDivision list based on other filters
                if (!filters.selectedDivs.length && !filters.selectedDraws.length) {
                    return team.divisions.some(d => filteredDivisions.some(fd => fd.id === d.id));
                }
                // if specific divisions are selected
                if (filters.selectedDivs.length) {
                    return team.divisions.some(d => filters.selectedDivs.some(sd => sd === d.id));
                }
                return true;
            });

        // SORTING
        let sortedPlayers = [];
        let sortedTeams = [];

        if (selectedDiv) {
            if (isSingles) {
                const playersList = filteredPlayers.map((player) => {
                    const playerDivisionObj = player.divisions.find(d => d.id === filters.selectedDivs[0]);
                    const playerPosition = getPlayerPosition(playerDivisionObj);
                    return {
                        ...player,
                        regStatus: `${getPlayerType(playerPosition)}`,
                        bucket: playerDivisionObj.bucket,
                        position: playerDivisionObj.position,
                        order: playerDivisionObj.order,
                    };
                });
                sortedPlayers = playersList.sort(comparator(getComparatorArguments(sortState.sortType, sortState.sortParam)));
            } else {
                const teamsList = filteredTeams.map((team) => {
                    const teamDivisionObj = team.divisions.find(d => d.id === filters.selectedDivs[0]);
                    const memberId1DivObj = playersDictByMemberId[team.members[0]]?.divisions?.find(div => div.id === teamDivisionObj.id);
                    const memberId2DivObj = playersDictByMemberId[team.members[1]]?.divisions?.find(div => div.id === teamDivisionObj.id);
                    let playerPosition;
                    let partnerPosition;
                    if (memberId1DivObj && memberId2DivObj) {
                        playerPosition = getPlayerPosition(memberId1DivObj);
                        partnerPosition = getPlayerPosition(memberId2DivObj);
                    }
                    if (playerPosition?.position > partnerPosition?.position) {
                        [team.members[0], team.members[1]] = [team.members[1], team.members[0]];
                    }
                    return {
                        ...team,
                        bucket: playerPosition?.bucket,
                        position: playerPosition?.position,
                        order: playerPosition?.order,
                    };
                });
                sortedTeams = teamsList.sort(comparator(getComparatorArguments(sortType.ASC)));
                let num = 1;
                sortedTeams = sortedTeams.map((team) => {
                    let type = '';
                    if (team.bucket === waitlistBucketTypeLabels.WAITLIST) {
                        type = team.position % 2 === 0 ? `#${team.position - num}` : `#${team.position - num + 1}`;
                        num += 1;
                    }

                    const regStatus = team.bucket ? `${team.bucket} ${type}` : '';
                    return {
                        ...team,
                        regStatus,
                    };
                });
            }
        }

        if (!selectedDiv) {
            if (isSingles) {
                sortedPlayers = sortPlayers({ players: filteredPlayers, sortState, acceptance, allPlayersTabFilter: filters.selectedDivs[0], organizerRatings, utrType });
            } else {
                // also add bucket, position, order, regStatus to teams when no division or multiple divisions are selected
                const populatedTeams = filteredTeams.map((team) => {
                    const teamDivisionObj = team.divisions?.[0]; // choose first division as representative
                    const memberId1DivObj = playersDictByMemberId[team.members[0]]?.divisions?.find(div => div.id === teamDivisionObj?.id);
                    const memberId2DivObj = playersDictByMemberId[team.members[1]]?.divisions?.find(div => div.id === teamDivisionObj?.id);
                    let playerPosition;
                    let partnerPosition;
                    if (memberId1DivObj && memberId2DivObj) {
                        playerPosition = getPlayerPosition(memberId1DivObj);
                        partnerPosition = getPlayerPosition(memberId2DivObj);
                    }
                    if (playerPosition?.position > partnerPosition?.position) {
                        [team.members[0], team.members[1]] = [team.members[1], team.members[0]];
                    }
                    const bucket = playerPosition?.bucket;
                    const position = playerPosition?.position;
                    const order = playerPosition?.order;
                    let regStatus = '';
                    if (bucket === waitlistBucketTypeLabels.WAITLIST) {
                        regStatus = `${bucket} ${position}`;
                    } else if (bucket) {
                        regStatus = `${bucket}`;
                    }
                    return {
                        ...team,
                        bucket,
                        position,
                        order,
                        regStatus,
                    };
                });

                sortedTeams = sortTeams({
                    teamsInDivision: populatedTeams,
                    playersDictByMemberId,
                    sortState,
                    organizerRatings,
                    utrType,
                });
            }
        }
        setMaxSortedTeams(sortedTeams);
        setMaxSortedPlayers(sortedPlayers);
        setFilteredSortedTeams(sortedTeams.slice(0, MAX_INITIAL_LOAD));
        setFilteredSortedPlayers(sortedPlayers.slice(0, MAX_INITIAL_LOAD));
        setHasLoadMoreButton(sortedPlayers.length > MAX_INITIAL_LOAD || sortedTeams.length > MAX_INITIAL_LOAD);
    }, [isSingles, filters?.selectedDivs, filters?.selectedDraws, filters?.notInDraw, playerListFilter, filters?.selectedDates, filters?.gender, sortState]);

    return {
        filteredSortedPlayers,
        filteredSortedTeams,
        maxSortedTeams,
        maxSortedPlayers,
        setSortState,
        sortState,
        hasLoadMore,
        setHasLoadMore,
        hasLoadMoreButton,
    };
};

export default useSortFilterPlayersTeams;
